import { Component, OnInit } from '@angular/core';
import { ClaimRequestService } from '../../services/claim.request.service';
import { TableComponent } from '../../../../common/components/table/table.component';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import { ClientButtonComponent } from '../../../../common/components/client-button/client-button.component';
import { ClientSelectorComponent } from '../../../../common/components/client-selector/client-selector.component';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { CommonModule, DatePipe } from '@angular/common';
import BaseListComponent from '../../../../common/components/base-list/base-list.component';
import Claim from '../../../../core/data/models/claim.model';
import ProducerClientDto from '../../../../core/data/dtos/producer-client.dto';
import { policyMask } from '../../../../common/constants/masks';
import { AuthService } from '../../../auth/services/auth.service';
import { HealthInsuredService } from '../../services/health.insured.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SwalService } from '../../../../common/services/swal.service';
import TableHeader from '../../../../common/interfaces/table/tableHeader';
import { lastValueFrom } from 'rxjs';
import ClaimRequest from '../../../../core/data/models/claim.request.model';
import { FormsModule } from '@angular/forms';
import HealthInsured from '../../../../core/data/models/health.insured.model';
import { INSURED_TYPE_CODE } from '../../utils/claimRequestConstants';
import { LocalPaginationService } from '../../../../common/services/local-pagination.service';
import { PlatformsService } from '../../../../core/data/services/platforms.service';


@Component({
  selector: 'app-claim-request-list',
  providers: [
    ClaimRequestService,
    HealthInsuredService,
    LocalPaginationService,
  ],
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    TableContainerComponent,
    ClientButtonComponent,
    ClientSelectorComponent,
    TableColumnDirective,
    DatePipe,
  ],
  templateUrl: './claim-request-list.component.html',
  styleUrl: './claim-request-list.component.css',
})
export class ClaimRequestListComponent
  extends BaseListComponent<Claim>
  implements OnInit {
  client?: ProducerClientDto;
  claimRequests: ClaimRequest[] = [];
  allClaimRequests: ClaimRequest[] = [];
  insuredList: HealthInsured[] = [];
  insuredHolder?: HealthInsured;
  selectedInsured?: HealthInsured;
  searchValue: string = '';
  protected readonly policyMask = policyMask;

  constructor(
    private claimRequestService: ClaimRequestService,
    private authService: AuthService,
    private healthInsuredService: HealthInsuredService,
    private platsformService: PlatformsService,
    private router: Router,
    private swalService: SwalService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  get tableHeaders() {
    const result: TableHeader[] = [
      { label: 'Fecha de servicio', attribute: 'claimDate' },
      { label: 'Estado de la solicitud', attribute: 'statusDescription' },
      { label: 'Número de solicitud', attribute: 'claimsRequestId' },
      { label: 'Nombre asegurado', attribute: 'name' },
      { label: 'Monto solicitado', attribute: 'billedAmount' },
      { label: 'Monto pagado', attribute: 'paidAmount' },
    ];

    return result;
  }

  get isIntermediary() {
    return this.route.snapshot.paramMap.get('idNumber') ? false : this.authService?.isIntermediary;
  }

  get isClient() {
    return this.route.snapshot.paramMap.get('idNumber') ? true : this.authService?.isClient;
  }

  override ngOnInit(): void {
    this.swalService.showLoading();
    const useTokenBridge = this.platsformService.isIOs() || this.platsformService.isEmbeddedApp();
    const idNumberFromRoute = this.route.snapshot.paramMap.get('idNumber');

    if (useTokenBridge && idNumberFromRoute) {
      this.getClaimRequests(idNumberFromRoute);
    } else {
      if (this.authService.user && !this.isIntermediary) {
        this.getClaimRequests();
      } else {
        // Embedded webview without a usable idNumber (e.g. hash fetch
        // failed) — dismiss the spinner so the empty state is visible.
        this.swalService.dismiss();
      }
    }
  }

  async getClaimRequests(idNumberParam?: string) {
    if (this.isIntermediary && !this.client) return;

    try {
      let idNumber: string | null | undefined;
      const useTokenBridge = this.platsformService.isIOs() || this.platsformService.isEmbeddedApp();

      if (idNumberParam) {
        idNumber = idNumberParam;
      } else if (useTokenBridge) {
        idNumber = this.route.snapshot.paramMap.get('idNumber');
      } else {
        const user = this.authService.user;
        idNumber = user?.idTokenClaims?.extension_IdNumber;
      }

      if (!idNumber) {
        this.swalService.dismiss();
        return;
      }

      const coreData = await lastValueFrom(
        this.healthInsuredService.getMyHealthInsured(idNumber)
      );

      if (coreData?.data?.length > 0) {
        this.insuredList = coreData.data;

        const holder = coreData.data?.find(
          (insured) => insured.dependent === INSURED_TYPE_CODE.HOLDER
        );

        this.insuredHolder = holder;

        this.selectedInsured = undefined;

        const result = await lastValueFrom(
          this.claimRequestService.getMyClaimsRequests(
            holder?.policyNumber,
            holder?.insured,
          )
        );

        this.claimRequests = result.data;
        this.allClaimRequests = result.data;

        this.swalService.dismiss();
      } else {
        this.swalService.dismiss();
      }
    } catch (e) {
      this.swalService.dismiss();
    }
  }

  onEndSearching(value: string) {
    this.searchValue = value;
    this.filterClaimRequests();
  }

  filterClaimRequests() {
    const term = this.searchValue.toLowerCase().trim();

    this.claimRequests = this.allClaimRequests.filter((request: any) => {
      return (
        request.claimsRequestId?.toString().includes(term) ||
        request.statusDescription?.toLowerCase().includes(term) ||
        request.billedAmount?.toString().includes(term) ||
        request.paidAmount?.toString().includes(term)
      );
    });
  }

  async onInsuredChange(selected?: HealthInsured) {
    this.swalService.showLoading();
    if (selected) {
      try {
        const result = await lastValueFrom(
          this.claimRequestService.getMyClaimsRequests(
            selected?.policyNumber,
            selected?.insured,
            selected?.dependent,
          ),
        );

        this.claimRequests = result.data;
        this.allClaimRequests = result.data;
      } catch (e) { }
      this.swalService.dismiss();
    } else {
      await this.getClaimRequests();
    }
  }

  addClaimRequests() {
    const useTokenBridge = this.platsformService.isIOs() || this.platsformService.isEmbeddedApp();
    let idNumber = useTokenBridge ? this.route.snapshot.paramMap.get('idNumber') : this.authService.user?.idTokenClaims?.extension_IdNumber;

    if (!useTokenBridge && this.isIntermediary) {
      idNumber = this.isIntermediary ? this.client!.idnumber : null;
    }
    if (useTokenBridge) {
      this.router.navigate(['/claim-requests/create'], {
        queryParams: {
          client: idNumber,
        },
      });
      return;
    }
    this.router.navigate(['/claim-request/create']);

  }

  handleSelectedClient($event: ProducerClientDto) {
    this.client = $event;
    this.getClaimRequests();
  }

}
